import { csvCell, downloadTextFile } from "../../../../utils/csv";

/**
 * Helpers for employee-import results.
 *
 * The API returns one result per (row, table): the Employee record plus one per
 * dependent table (personal details, addresses, contacts, government documents...).
 */

export const ROW_STATUS = {
  SUCCESS: "SUCCESS",
  PARTIAL: "PARTIAL",
  FAILED: "FAILED",
};

const isFailed = (r) => r.status === "FAILED";
const isPartial = (r) => r.status === "PARTIAL SUCCESS";

/**
 * Group results by row → [{ row, name, employeeId, status, items }]
 * Row status follows the API's stats:
 *   FAILED  = the Employee record itself failed
 *   PARTIAL = employee saved, but some dependent record failed
 *   SUCCESS = everything saved
 */
export const groupResultsByRow = (results = []) => {
  const byRow = new Map();
  results.forEach((r) => {
    if (!byRow.has(r.row)) byRow.set(r.row, []);
    byRow.get(r.row).push(r);
  });

  return Array.from(byRow, ([row, items]) => {
    const employeeItem = items.find((i) => i.table === "Employee") || items[0];
    let status = ROW_STATUS.SUCCESS;
    if (employeeItem && isFailed(employeeItem)) status = ROW_STATUS.FAILED;
    else if (items.some((i) => isFailed(i) || isPartial(i)))
      status = ROW_STATUS.PARTIAL;

    const employeeId =
      items.map((i) => i.employeeId).find((id) => id && id !== "N/A") || "";
    return {
      row,
      name: employeeItem?.details?.fullName || "Unknown employee",
      employeeId,
      status,
      items,
    };
  }).sort((a, b) => a.row - b.row);
};

export const countByStatus = (groups) =>
  groups.reduce((acc, g) => ({ ...acc, [g.status]: acc[g.status] + 1 }), {
    [ROW_STATUS.SUCCESS]: 0,
    [ROW_STATUS.PARTIAL]: 0,
    [ROW_STATUS.FAILED]: 0,
  });

/**
 * CSV of the problem items only (FAILED and PARTIAL SUCCESS): Row, Name, Table, Message.
 * Prefixed with a BOM so Excel opens accented names (ñ) correctly.
 */
export const buildErrorCsv = (results = []) => {
  const header = ["Row", "Name", "Table", "Message"];
  const lines = results
    .filter((r) => isFailed(r) || isPartial(r))
    .sort((a, b) => a.row - b.row)
    .map((r) =>
      [r.row, r.details?.fullName || "", r.table || "", r.message || ""]
        .map(csvCell)
        .join(","),
    );
  return "﻿" + [header.map(csvCell).join(","), ...lines].join("\r\n");
};

export const hasProblems = (results = []) =>
  results.some((r) => isFailed(r) || isPartial(r));

export { downloadTextFile };

/* ---------- Remember the running batch (survives refresh / navigation) ---------- */
const ACTIVE_BATCH_KEY = "employeeImport:activeBatch";

export const activeImportStorage = {
  get: () => {
    try {
      return JSON.parse(sessionStorage.getItem(ACTIVE_BATCH_KEY) || "null");
    } catch {
      return null;
    }
  },
  set: (batch) => {
    try {
      sessionStorage.setItem(ACTIVE_BATCH_KEY, JSON.stringify(batch));
    } catch {
      /* storage unavailable: the URL still holds the batch id */
    }
  },
  clear: () => {
    try {
      sessionStorage.removeItem(ACTIVE_BATCH_KEY);
    } catch {
      /* ignore */
    }
  },
};

/** The 409 message includes the running batch id: "... already running (batch <uuid>)". */
export const extractBatchIdFromMessage = (message = "") =>
  (String(message).match(/batch\s+([0-9a-f]{8}-[0-9a-f-]{27,})/i) || [])[1] ||
  null;
